import type { ProductEditAction } from "@v2/features/products/ProductEditContext";
import type { BlockerFunction } from "react-router-dom";
import type { Product, ProductFormSchema } from "schemas/generated/product";

import React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Loader } from "@v2/components/Loader";
import { useProductDetails } from "@v2/features/products/hooks/useProductDetails";
import { useProductUpdate } from "@v2/features/products/hooks/useProductUpdate";
import { ProductFormUnsavedChangesModal } from "@v2/features/products/modals/ProductFormUnsavedChangesModal";
import { ProductEditContext } from "@v2/features/products/ProductEditContext";
import { getProductFormData } from "@v2/features/products/utils";
import { useForm } from "react-hook-form";
import { Outlet, useBlocker } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { productFormSchema } from "schemas/product";

const LoadedProductEditWrapper: React.FC<{
  product: Product;
  creatingNewProduct: boolean;
}> = ({ product, creatingNewProduct }) => {
  const { isLoading: isUpdating } = useProductUpdate();

  const formData = {
    ...getProductFormData(product),
    uploadingFileIds: [],
  };

  const {
    handleSubmit,
    formState: { errors, isDirty, isValid, isSubmitting, isSubmitted },
    reset,
    getValues,
    setValue,
    watch,
  } = useForm<ProductFormSchema>({
    resolver: zodResolver(productFormSchema),
    defaultValues: formData,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const shouldBlock = React.useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) => {
      const uploadingFileIds = getValues("uploadingFileIds") ?? [];
      const isUploadingFiles = uploadingFileIds.length > 0;
      return (
        (isDirty || isUploadingFiles) &&
        currentLocation.pathname !== nextLocation.pathname &&
        !isSubmitting
      );
    },
    [isDirty, isSubmitting, getValues],
  );

  const blocker = useBlocker(shouldBlock);

  const handleSetValue = (
    key: keyof ProductFormSchema,
    value: ProductFormSchema[keyof ProductFormSchema],
    shouldDirty = true,
  ) => {
    setValue(key, value, {
      shouldDirty,
      shouldValidate: true,
    });
  };

  const dispatchProductEdit = (action: ProductEditAction) => {
    exhaustiveSwitch(
      action,
      "type",
    )({
      changeProperty: (action) => {
        const { key, value, shouldDirty } = action.payload;
        handleSetValue(key, value, shouldDirty);
      },
      updateMedia: (action) => {
        const { media, shouldDirty } = action.payload;
        handleSetValue("media", media, shouldDirty);
      },
      appendMedia: (action) => {
        const { newMediaItem, shouldDirty } = action.payload;
        const currentMedia = getValues("media") ?? [];
        const updatedMedia = [...currentMedia, newMediaItem];
        handleSetValue("media", updatedMedia, shouldDirty);
      },
      addUploadingFileId: (action) => {
        const { fileId } = action.payload;
        const currentIds = getValues("uploadingFileIds") ?? [];
        const updatedIds = [...currentIds, fileId];
        handleSetValue("uploadingFileIds", updatedIds, false);
      },
      removeUploadingFileId: (action) => {
        const { fileId } = action.payload;
        const currentIds = getValues("uploadingFileIds") ?? [];
        const updatedIds = currentIds.filter((id) => id !== fileId);
        handleSetValue("uploadingFileIds", updatedIds, false);
      },
      updateProductEmail: (action) => {
        const { productEmail, shouldDirty } = action.payload;
        handleSetValue("productEmail", productEmail, shouldDirty);
      },
    });
  };

  return (
    <ProductEditContext.Provider
      value={{
        watch,
        dispatchProductEdit,
        reset,
        hasDataChanged: isDirty,
        isProductValid: isValid,
        errors,
        getValues,
        isLoading: isUpdating,
        creatingNewProduct,
        isSubmitting,
        isSubmitted,
        handleReactHookFormSubmit: handleSubmit,
        product,
      }}
    >
      <Outlet />
      {blocker.state === "blocked" &&
        (isDirty || (getValues("uploadingFileIds") ?? []).length > 0) && (
          <ProductFormUnsavedChangesModal
            blocker={blocker}
            uploadingFileIds={getValues("uploadingFileIds") ?? []}
          />
        )}
    </ProductEditContext.Provider>
  );
};

export function ProductEditWrapper() {
  const { product, isLoading, creatingNewProduct } = useProductDetails();

  if (!product || isLoading) {
    return <Loader />;
  }

  return (
    <LoadedProductEditWrapper
      product={product}
      creatingNewProduct={creatingNewProduct}
    />
  );
}
